import { Validators, AbstractControl, FormGroup } from '@angular/forms';
import { Injectable, Injector } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Resolve, UrlSegment, UrlMatchResult, DefaultUrlSerializer, UrlTree, Router } from '@angular/router';
import { AtpDateTimeService } from './components/atp-range-date-time-picker/atp-date-time.service';
import { HubConnection, HubConnectionBuilder } from '@aspnet/signalr';
import { Observable, Subject, Subscription } from 'rxjs';
import { AtpHttpService, IAtpUser } from './services/atp-http.service';
import { environment } from 'src/environments/environment';
import { AtpHttpErrorsService } from './services/atp-http-errors.service';
import { ComponentType } from '@angular/cdk/overlay/index';
import * as signalR from '@aspnet/signalr';

export let AppInjector: Injector;

export function setAppInjector(injector: Injector) {
  if (AppInjector) {
    console.error('Programming error: AppInjector was already set');
  }
  else {
    AppInjector = injector;
  }
}

export class AtpValidators extends Validators {
  static positiveNumber(fieldControl: AbstractControl): { [key: string]: boolean } {
    return +fieldControl.value > 0 ? null : { positivenumber: true };
  }

  static checkPassLanguage(fieldControl: AbstractControl): { [key: string]: boolean } {
    return (<string>fieldControl.value).search(/^[a-zA-Z0-9]+$/) != -1 ? null : { checkpasslanguage: true };
  }

  static stringLength(maxLength: number, minLength: number): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      return <string>fieldControl.value && ((<string>fieldControl.value).length > maxLength || (<string>fieldControl.value).length < minLength) ? { stringlength: true } : null;
    }
  }

  static minValue(minValue: number): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      return !minValue || (!isNaN(+fieldControl.value) && +fieldControl.value >= minValue) ? null : { minvalue: true };
    }
  }

  static maxValue(maxValue: number): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      return !maxValue || (!isNaN(+fieldControl.value) && +fieldControl.value <= maxValue) ? null : { maxvalue: true };
    }
  }

  static regularExpression(pattern: string | RegExp, name: string): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    const reg = pattern instanceof RegExp ? pattern : new RegExp(pattern);
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      return !<string>fieldControl.value || reg.test(<string>fieldControl.value) ? null : { [name.toLowerCase()]: true };
    }
  }

  static compare(form: FormGroup, otherProperty: string): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    let subscription: Subscription;
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      if (!subscription) {
        subscription = form.controls[otherProperty].valueChanges.subscribe(
          (value) => {
            fieldControl.setValue(fieldControl.value);
            fieldControl.markAsTouched();
          }
        );
      }
      return form.controls[otherProperty] && (<string>form.controls[otherProperty].value) == (<string>fieldControl.value) ? null : { compare: true };
    }
  }

  static checkFiles(): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      return fieldControl.value != 'Invalid' ? null : { checkFiles: true };
    }
  }

  static inn(): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      let value = fieldControl.value;
      let regexpNumber: RegExp = /^[+0-9]{10}$|^[+0-9]{12}$/;

      if (regexpNumber.test(value)) {
        if (value.length == 10) {
          var n10 = AtpValidate.checkDigit(value, [2, 4, 10, 3, 5, 9, 4, 6, 8]);
          if (n10 === parseInt(value[9])) {
            return null;
          }
        }
        else {
          var n11 = AtpValidate.checkDigit(value, [7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
          var n12 = AtpValidate.checkDigit(value, [3, 7, 2, 4, 10, 3, 5, 9, 4, 6, 8]);
          if ((n11 === parseInt(value[10])) && (n12 === parseInt(value[11]))) {
            return null;
          }
        }
      }

      return { inn: true };
    }
  }

  static multipleAutocompleteRequired(multipleAutocompleteValues: { [key: string]: Array<IAtpSelectItem> }, propertyName: string): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      return multipleAutocompleteValues[propertyName] && multipleAutocompleteValues[propertyName].length ? null : { multipleautocompleterequired: true };
    }
  }

  // TODO: рефакторинг
  static rangeDateTime(dateTimeService: AtpDateTimeService, type: string): (fieldControl: AbstractControl) => { [key: string]: boolean } {
    return (fieldControl: AbstractControl): { [s: string]: boolean } => {
      if (fieldControl.value == 'INVALID') {
        return { rangedatetime: true };
      }

      return !fieldControl.value || (fieldControl.value.length == 2 && fieldControl.value[0] <= fieldControl.value[1]) ? null : { rangedatetime: true };
    }
  }
}

class AtpValidate {
  public static checkDigit(inn: number, coefficients: number[]) {
    let n = 0;

    for (var i in coefficients) {
      n += coefficients[i] * inn[i];
    }

    return n % 11 % 10;
  }
}

@Injectable()
export class AtpLowerCaseUrlSerializer extends DefaultUrlSerializer {
  parse(url: string): UrlTree {
    let urlParts = url.split('?');
    return super.parse(urlParts[0].toLowerCase() + (urlParts[1] ? '?' + urlParts[1] : ''));
  }
}

export interface IAtpMergeParamsAndQueryParamsResolverResult {
  params: any;
  queryParams: any;
  queryParamsString: string;
  backUrl: string;
}

@Injectable()
export class AtpMergeParamsAndQueryParamsResolver implements Resolve<IAtpMergeParamsAndQueryParamsResolverResult> {
  constructor(private router: Router) { }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): IAtpMergeParamsAndQueryParamsResolverResult {
    return { params: route.params, queryParams: route.queryParams, queryParamsString: state.url.split('?')[1], backUrl: this.router.url };
  }
}

export function optionalRouteHandler(path: string, urlSegments: UrlSegment[]): UrlMatchResult {
  let parts = path.split('/');
  if (parts.length < urlSegments.length) return null;

  let posParams: { [name: string]: UrlSegment; } = {};
  for (let i = 0; i < parts.length; i++) {
    if (parts[i].substr(0, 1) == ':' && parts[i].substr(-1, 1) == '?') {
      if (urlSegments[i]) {
        posParams[parts[i].substr(1, parts[i].length - 2)] = urlSegments[i];
      }
    } else if (parts[i].substr(0, 1) == ':') {
      if (!urlSegments[i]) {
        return null;
      }
      posParams[parts[i].substr(1, parts[i].length - 1)] = urlSegments[i];
    } else {
      if (!urlSegments[i] || urlSegments[i].path != parts[i]) {
        return null;
      }
    }
  }
  return {
    consumed: urlSegments,
    posParams: posParams
  };
}

// История

export interface IAtpHistory {
  id: string;
  action: string;
  date: string;
  userId: string;
  userName?: string;
  entityId: string;
  urlPath: string;
  json: any;
  haveJson: boolean;
}

export class AtpHistoryData {
  constructor(public baseGroupName: string, public type: string, public entityId: string, public items: IAtpPagination<IAtpHistory>, public backUrl: string, public isShared: boolean = false, public canOpenRemoveItem: boolean = true) { }
}

export class AtpHistoryDetailData {
  constructor(public model: IAtpHistory, public backUrl: string) { }
}

// DynamicTableFullCrud

export interface IAtpTableInfo {
  title: string;
  active: string;
  direction: string;
  isTree: boolean;
}

export interface IAtpPropertyInfo {
  name: string;
  display: string;
  order: number;
  format: string;
  isNotSort: boolean;
  isHidden: boolean;
  columnType: string;
  params: any[];
  filtersInfo: IAtpFilterInfo[];
}

export interface IAtpFilterInfo {
  name: string;
  display: string;
  groupName: string;
  groupDisplay: string;
  icon: string;
  defaultValue: any;
  type: string;
  selectItems: IAtpSelectItem[];
}

export interface IAtpAddEditInfo {
  title: string;
  icon: string;
  properties: IAtpAddEditPropertyInfo[];
  groups: IAtpAddEditGroupPropertyInfo[];
  model: any;
}

export interface IAtpAddEditGroupPropertyInfo {
  name: string;
  display: string;
  type: string;
  params: any[];
  order: string;
  groupName: string;
  bind: string;
  isVisible: boolean;
  items: Array<IAtpAddEditGroupPropertyInfo | IAtpAddEditPropertyInfo>;
  form: FormGroup;
  isGroup: boolean;
}

export interface IAtpAddEditPropertyInfo {
  name: string;
  display: string;
  controlType: string;
  params: any[];
  order: number;
  isHidden: boolean;
  defaultValue: any;
  validationsInfo: IAtpValidationInfo[];
  selectItems: any[];
  groupName: string;
  componentAfter: string;
  componentBefore: string;
}

export interface IAtpValidationInfo {
  name: string;
  errorMessage: string;
  params: any[];
}

export interface IAtpSelectItem {
  id: any;
  value: string;
}

export interface IAtpPagination<T> {
  allCount: number;
  pageSize: number;
  pagesCount: number;
  pageNumber: number;
  items: T[]
}

export interface IAtpPaginationDynamicTable<T> extends IAtpPagination<T> {
  tableInfo: IAtpTableInfo;
  propertiesInfo: IAtpPropertyInfo[];
}

export interface IAtpPaginationDynamicTreeTable<T> extends IAtpPaginationDynamicTable<T> {
  breadCrumbs: IAtpSelectItem[];
}

export interface IAtpEntity {
  id: any;
  name: string;
}

export interface IAtpAnnotations {
  name?: string;
  baseGroupName?: string;
  entryComponents?: { [key: string]: ComponentType<{}> };
  access?: IAtpAccess;
  rootBackUrl?: string;
  isPopup?: boolean;
}

export interface IAtpAccess {
  name: string;
}

export function AtpDynamicTableFullCrud(annotations: IAtpAnnotations): any {
  return (target: any): any => {
    if (annotations.rootBackUrl && annotations.rootBackUrl.startsWith('/')) {
      annotations.rootBackUrl = annotations.rootBackUrl.substr(1);
    }

    let entryComponents = {};
    for (const key in annotations.entryComponents) {
      if (annotations.entryComponents.hasOwnProperty(key)) {
        const element = annotations.entryComponents[key];
        entryComponents[key.toUpperCase()] = element;
      }
    }
    annotations.entryComponents = entryComponents;
    target.__atpannotations__ = annotations;
    return target;
  }
}

export class AtpAddEditData<TModel, TInfo> {
  constructor(public type: string, public model: TModel, public info: TInfo, public settings?: IAtpAddEditDataSettings, public params?: any) {
    if (!settings) {
      settings = {};
    }
    settings.actionAddIcon = settings.actionAddIcon || 'add';
    settings.actionEditIcon = settings.actionEditIcon || 'mode_edit';
  }
}

/**
 * Настройки добавления/редактирования
 */
export interface IAtpAddEditDataSettings {
  isTree?: boolean;
  baseGroupName?: string;
  isNotResultPopup?: boolean;
  addApiName?: string;
  editApiName?: string;
  confirmButtonAddName?: string;
  confirmButtonEditName?: string;
  closedButtonName?: string;
  confirmButtonIsInvisible?: boolean;
  closedButtonIsInvisible?: boolean;
  actionAddIcon?: string;
  actionEditIcon?: string;
}

export class AtpAutocompleteItem implements IAtpSelectItem {
  id: any;
  value: string;
  constructor();
  constructor(obj: IAtpSelectItem);
  constructor(id: any, value: string);
  constructor(obj?: any, value?: string) {
    if (obj === undefined && value === undefined) {
      this.id = null;
      this.value = '';
    }
    else if (this.instanceOfIAtpSelectItem(obj)) {
      this.id = obj.id;
      this.value = !obj.value ? '' : value;
    }
    else {
      this.value = !value ? '' : value;
    }
  }

  private instanceOfIAtpSelectItem(object: any): object is IAtpSelectItem {
    return 'id' in object && 'value' in object;
  }

  toString(): string {
    return this.value;
  }
}

export class EventArgs<T, D> {
  constructor(public sender?: T, public e?: D) { }
}

export interface IAtpTableSortInfo {
  Active: string;
  Direction: string;
}



export abstract class AtpFiltersPanelItem<T> {
  constructor(type: string) {
    this.type = type;
  }

  name: string;
  type: string;
  value: T;
  defaultValue: T;
  display?: string;
}

export class AtpFiltersPanelGroup extends AtpFiltersPanelItem<any> {
  constructor(info: IAtpFilterInfo, params: any);
  constructor(name: string, items: AtpFiltersPanelItem<any>[], display?: string);
  constructor(param1: any, param2: any, display?: string) {
    super('Group');
    if (typeof (param1) == 'string') {
      this.name = param1;
      this.items = param2;
      this.display = display;
    }
    else {
      this.name = (<IAtpFilterInfo>param1).name;
      this.display = (<IAtpFilterInfo>param1).display;

      for (const filter of param2.flatFiltersInfo) {
        if (filter.groupName == this.name) {
          this.items.push(getFilterItem(filter, param2.flatFiltersInfo, param2.component));
        }
      }
    }
  }

  items: AtpFiltersPanelItem<any>[] = [];
}

export class AtpFiltersPanelBoolean extends AtpFiltersPanelItem<boolean> {
  constructor(info: IAtpFilterInfo, params: any);
  constructor(name: string);
  constructor(name: string, display: string);
  constructor(name: string, display: string, defaultValue: boolean);
  constructor(param1: any, param2?: any, public defaultValue: boolean = false) {
    super('Boolean');
    if (typeof (param1) == 'string') {
      this.name = param1;
      this.display = param2;
      this.defaultValue = !!defaultValue;
    }
    else {
      this.name = (<IAtpFilterInfo>param1).name;
      this.display = (<IAtpFilterInfo>param1).display;
      this.defaultValue = (<IAtpFilterInfo>param1).defaultValue;
    }
    this.value = this.defaultValue;
  }
}

export class AtpFiltersPanelInput extends AtpFiltersPanelItem<string | number> {
  constructor(info: IAtpFilterInfo, params: any);
  constructor(name: string);
  constructor(name: string, display: string);
  constructor(name: string, display: string);
  constructor(name: string, display: string, defaultValue: string | number);
  constructor(name: string, display: string, defaultValue: string | number, type: 'String' | 'Number');
  constructor(name: string, display: string, defaultValue: string | number, type: 'String' | 'Number', mask: (string | RegExp)[]);
  constructor(param1: any, param2?: any, defaultValue: string | number = '', type: 'String' | 'Number' = 'String', mask?: (string | RegExp)[]) {
    super(type);
    if (typeof (param1) == 'string') {
      this.name = param1;
      this.display = param2;
      this.defaultValue = defaultValue;
      this.mask = mask ? mask.map(x => {
        if (typeof (x) == 'string') {
          if (x.length > 1) {
            x = new RegExp(x.replace(/\//g, ''));
          }
        }
        return x;
      }) : null;
    }
    else {
      this.name = (<IAtpFilterInfo>param1).name;
      this.display = (<IAtpFilterInfo>param1).display;
      this.defaultValue = (<IAtpFilterInfo>param1).defaultValue;
      this.mask = param1 && param1.mask ? param1.mask.map(x => {
        if (typeof (x) == 'string') {
          if (x.length > 1) {
            x = new RegExp(x.replace(/\//g, ''));
          }
        }
        return x;
      }) : null;
    }
    this.value = this.defaultValue;
  }

  mask: (string | RegExp)[]
}

export class AtpFiltersPanelSelect extends AtpFiltersPanelItem<IAtpSelectItem> {
  constructor(info: IAtpFilterInfo, params: any);
  constructor(name: string);
  constructor(name: string, options: IAtpSelectItem[]);
  constructor(name: string, options: IAtpSelectItem[], display: string);
  constructor(name: string, options: IAtpSelectItem[], display: string, defaultValue: IAtpSelectItem);
  constructor(param1: any, param2?: any, display?: string, defaultValue: IAtpSelectItem = undefined) {
    super('Select');
    if (typeof (param1) == 'string') {
      this.name = param1;
      this.options = param2;
      this.display = display;
      this.defaultValue = defaultValue;
    }
    else {
      this.type = (<IAtpFilterInfo>param1).type;
      this.name = (<IAtpFilterInfo>param1).name;
      this.options = (<IAtpFilterInfo>param1).selectItems;
      this.display = (<IAtpFilterInfo>param1).display;
      this.defaultValue = (<IAtpFilterInfo>param1).defaultValue;
    }
    this.value = this.defaultValue;
  }

  options: IAtpSelectItem[];
}

export class AtpFiltersPanelDateTime extends AtpFiltersPanelItem<string[]> {
  constructor(info: IAtpFilterInfo, params: any);
  constructor(name: string);
  constructor(name: string, display: string);
  constructor(name: string, display: string, defaultValue: string[]);
  constructor(param1: any, param2?: any, public defaultValue: string[] = null) {
    super('DateTime')

    if (typeof (param1) == 'string') {
      this.name = param1;
      this.display = param2;
      this.defaultValue = defaultValue;
    }
    else {
      this.name = (<IAtpFilterInfo>param1).name;
      this.display = (<IAtpFilterInfo>param1).display;
      this.defaultValue = (<IAtpFilterInfo>param1).defaultValue;
    }
    this.value = this.defaultValue;
  }
}

// export class AtpFiltersPanelAutocomplete extends AtpFiltersPanelItem<AtpAutocompleteItem> {
//   constructor(public name: string, public autocompleteEventHandler: (event: any) => void, public label?: string, public icon?: string, public defaultValue: AtpAutocompleteItem = new AtpAutocompleteItem(), public passTime: number = 200) {
//     this.value = new AtpAutocompleteItem(this.defaultValue.value, this.defaultValue.id);
//   }
//   readonly type: string = 'Autocomplete';
//   private _value: AtpAutocompleteItem;
//   get value(): AtpAutocompleteItem {
//     return this._value;
//   }
//   set value(value: AtpAutocompleteItem) {
//     this._value = new AtpAutocompleteItem(value.value, value.id);
//   }
// }

function getFilterItem(filterInfo: IAtpFilterInfo, flatFiltersInfo: IAtpFilterInfo[], component: any): AtpFiltersPanelItem<any> {
  let result: AtpFiltersPanelItem<any>;
  switch (filterInfo.type) {
    case 'Group':
      result = new AtpFiltersPanelGroup(filterInfo, { flatFiltersInfo, component });
      break;
    case 'Boolean':
      result = new AtpFiltersPanelBoolean(filterInfo, { flatFiltersInfo, component });
      break;
    case 'Number':
    case 'String':
      result = new AtpFiltersPanelInput(filterInfo, { flatFiltersInfo, component });
      break;
    case 'Select':
      result = new AtpFiltersPanelSelect(filterInfo, { flatFiltersInfo, component });
      break;
    case 'DateTime':
      result = new AtpFiltersPanelDateTime(filterInfo, { flatFiltersInfo, component });
      break;
    // case 'Autocomplete':
    //   result = new AtpFiltersPanelAutocomplete(
    //     filterInfo.name,
    //     (<(event: any) => void>component[filterInfo.name + 'AutocompleteEventHandler']).bind(component),
    //     filterInfo.display,
    //     filterInfo.icon,
    //     filterInfo.defaultValue ? new AtpAutocompleteItem(filterInfo.defaultValue[0], filterInfo.defaultValue[1]) : new AtpAutocompleteItem()
    //   );
    //   break;
  }
  return result;
}

export function getFiltersPanelItems(propertiesInfo: IAtpPropertyInfo[], component: any): AtpFiltersPanelItem<any>[] {
  let flatFilterItems: IAtpFilterInfo[] = [];

  for (const property of propertiesInfo) {
    if (property.filtersInfo) {
      for (const item of property.filtersInfo) {
        if (item.selectItems && item.selectItems.length) {
          item.selectItems.forEach(element => {
            element.toString = () => element.value;
          });
        }
      }
    }
    flatFilterItems = flatFilterItems.concat(property.filtersInfo);
  }

  let result: AtpFiltersPanelItem<any>[] = [];
  for (const filter of flatFilterItems) {
    if (!filter.groupName) {
      result.push(getFilterItem(filter, flatFilterItems, component));
    }
  }
  return result;
}




export class AtpSignalRHubEvent<T> extends Subject<T> {
  constructor(public name: string, public register?: (data: any) => T, public unregister?: () => void) {
    super();
  }
}

export class AtpSignalRHubConnection {
  protected connection: HubConnection;

  constructor(public name: string, protected api: AtpHttpService<IAtpUser>, protected httpErrors: AtpHttpErrorsService, protected events: AtpSignalRHubEvent<any>[] = [], public serverReadyDelayMs: number = 1000) { }

  getEvent<T>(name: string): Observable<T> {
    const result = this.events.find(x => x.name == name);
    return result ? (<Observable<T>>result.asObservable()) : null;
  }

  unsubscribeAllEvents() {
    for (const event of this.events) {
      event.observers.splice(0, event.observers.length);
    }
  }

  protected create(): void {
    this.connection = new HubConnectionBuilder()
      .configureLogging(signalR.LogLevel.None)
      .withUrl((<any>this.api).baseAddress + 'Hubs/' + this.name, {
        accessTokenFactory: () => this.api.token
      })
      .build();
  }

  protected registerEvents(): void {
    for (const event of this.events) {
      this.connection.on(event.name, (data: string) => {
        if (event.register) {
          event.register(data);
        }
        event.next(data);
      });
    }
  }

  protected unregisterEvents(): void {
    for (const event of this.events) {
      if (event.unregister) {
        event.unregister();
      }

      this.connection.off(event.name);
    }
  }

  isConnection: boolean = false;

  public start(initName?: string, ...args: any[]): void {
    if (this.connection) {
      this.stop();
    }
    this.create();
    this.registerEvents();
    setTimeout(() => {
      this.connection.start().then(() => {
        this.isConnection = true;
        if (!environment.production) {
          console.log('Hub ' + this.name + ' connection started');
        }

        if (initName) {
          this.connection.invoke(initName, ...args);
        }

        // Событие сброса подключения
        this.connection.onclose((err) => {
          this.stop();
          if (err) {
            if (this.api.token) {
              this.start();
            }
          }
        });
      }).catch((err) => {
        if (('' + err).replace('Error: ', '') == "Unauthorized") {
          this.httpErrors.reauth(() => { this.start(initName, args); }, null);
          if (!environment.production) {
            console.log('Hub ' + this.name + ' ' + err);
          }
          return;
        }
        if (!environment.production) {
          console.log('Hub ' + this.name + ' ' + err);
        }
        this.connection = null;
      });
    }, this.serverReadyDelayMs);
  }

  public stop(): void {
    if (this.connection) {
      this.unregisterEvents();
      this.connection.stop();
      this.connection = null;
      if (!environment.production) {
        console.log('Hub ' + this.name + ' connection stoped');
      }
    }
  }
}

export class Guid {
  constructor(value: string) {
    value = value.toUpperCase();
    let reg = RegExp('[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}');
    if (reg.exec(value)) {
      this._value = value;
    }
  }

  private _value: string;

  public toString(): string {
    return this._value;
  }

  static newGuid(): Guid {
    var result: string;
    var i: string;
    var j: number;

    result = "";
    for (j = 0; j < 32; j++) {
      if (j == 8 || j == 12 || j == 16 || j == 20) {
        result = result + '-';
      }
      i = Math.floor(Math.random() * 16).toString(16).toUpperCase();
      result = result + i;
    }
    return new Guid(result);
  }
}
